/**
 * Extract step IDs referenced by a step's configuration via Expression
 * trees and conditional builder rows.
 *
 * Covers two leaf shapes:
 *   - Built Expression nodes: `{ kind: 'variable', stepID }` (produced by
 *     `conditionalsBuilder` and used in argument bindings / SetEnvironmentVariable).
 *   - Conditional builder rows: variable descriptors of the form
 *     `{ kind: 'step', stepID, variableName, ... }` nested under a `variable`
 *     key inside the 2D `Conditional[][]` stored as `args.condition` on
 *     If/ElseIf/Loop/Wait/RunSkill/Antigravity steps.
 *
 * Also parses JSON-serialized strings and recurses into the parsed tree.
 * CodeBlock Python source is not scanned — dynamic variable access in code
 * is a known limitation.
 */

function tryParseJson(value: string): unknown {
  const trimmed = value.trim();

  if (trimmed.length === 0) return undefined;

  const first = trimmed[0];

  if (first !== '{' && first !== '[') return undefined;

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

/**
 * Recursively walk an unknown value and collect `stepID` values from any
 * node matching the `{ kind: 'variable', stepID: string }` shape. String
 * values that look like JSON objects/arrays are parsed and walked too.
 */
export function getStepReferencedStepIDs(
  node: unknown,
  out: Set<string> = new Set(),
): Set<string> {
  if (node == null) return out;

  if (typeof node === 'string') {
    const parsed = tryParseJson(node);

    if (parsed != null) getStepReferencedStepIDs(parsed, out);

    return out;
  }

  if (typeof node !== 'object') return out;

  const rec = node as Record<string, unknown>;

  if (
    (rec.kind === 'variable' || rec.kind === 'step') &&
    typeof rec.stepID === 'string'
  ) {
    out.add(rec.stepID);
  }

  for (const value of Object.values(rec)) {
    getStepReferencedStepIDs(value, out);
  }

  return out;
}
